import {
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  CsvExportModule,
  ModuleRegistry,
  RenderApiModule,
  SetFilterModule,
  ValidationModule,
} from 'ag-grid-enterprise';
import { ExternalFilterModule, type ColDef, type GridApi } from 'ag-grid-community';
import { AgGridBase } from 'Components/organisms/AgGridBase';
import { DEFAULT_BASE_COL_DEF, DEFAULT_GRID_SIZING } from 'Components/organisms/AgGridBase/AgGridBase.config';
import { useCallback, useMemo, useRef } from 'react';
import { getPhishingCampaignColumns } from './getPhishingCampaignColumns';
import { getPhishingRecipientColumns } from './getPhishingRecipientColumns';
import type { PhishingCampaignRow, PhishingRecipientRow } from './phishingCampaignsTable.types';
import { EmptyState } from 'Components/molecules/EmptyState';
import EngageIcon from 'Assets/icons/engagement.svg';
import { useI18n } from 'Hooks/useI18n';
import { useClientExternalTextSearch } from 'Components/organisms/AgGridBase';

ModuleRegistry.registerModules([
  ExternalFilterModule,
  SetFilterModule,
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  CsvExportModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
]);

const TABLE_BODY_HEIGHT = '320px';

type LabelFn = (key: string, fallback: string) => string;

type PhishingCampaignManagementAgGridProps = {
  label: LabelFn;
  rowData: PhishingCampaignRow[];
  loading: boolean;
  onSearchChange: (searchValue: string) => void;
  onSend: (id: string) => void;
  statusFilterValues: readonly string[];
};

export function PhishingCampaignManagementAgGrid({
  label,
  rowData,
  loading,
  onSearchChange,
  onSend,
  statusFilterValues,
}: PhishingCampaignManagementAgGridProps) {
  const gridApiRef = useRef<GridApi<PhishingCampaignRow> | null>(null);
  const { t } = useI18n();
  const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
    useClientExternalTextSearch<PhishingCampaignRow>({
      fields: [(row) => row.name, (row) => row.subject],
      onSearchChange,
      onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
    });

  const columnDefs = useMemo(
    () =>
      getPhishingCampaignColumns({
        label,
        onSend,
        statusFilterValues,
      }),
    [label, onSend, statusFilterValues],
  );

  return (
    <AgGridBase.Root fullWidth>
      <AgGridBase.Header hidden />
      <AgGridBase.Table<PhishingCampaignRow>
        columnDefs={columnDefs}
        dataMode="client"
        rowData={rowData}
        showSidebar={false}
        movableColumns={false}
        bodyHeight={TABLE_BODY_HEIGHT}
        bodyMinHeight={DEFAULT_GRID_SIZING.minHeight}
        defaultColDef={DEFAULT_BASE_COL_DEF}
        showSearchBar={true}
        onSearchChange={handleSearchChange}
        agGridProps={{
          loading,
          getRowId: (p) => String(p.data?.id ?? ''),
          isExternalFilterPresent,
          doesExternalFilterPass,
          onGridReady: (e) => {
            gridApiRef.current = e.api;
          },
          noRowsOverlayComponent: EmptyState,
          noRowsOverlayComponentParams: {
            ImageSrc: EngageIcon,
            noDescription: false,
            description: t('PHISHING_CAMPAIGNS_NO_ROWS_OVERLAY_DESCRIPTION'),
          },
        }}
      />
    </AgGridBase.Root>
  );
}

type PhishingCampaignRecipientsAgGridProps = {
  label: LabelFn;
  rowData: PhishingRecipientRow[];
  loading: boolean;
  getStatusLabel: (status: string) => string;
  statusFilterValues: readonly string[];
  onSearchChange: (searchValue: string) => void;
  onGridApi: (api: GridApi<PhishingRecipientRow> | null) => void;
  onViewEmailBody: (body: string, row: PhishingRecipientRow) => void;
};

export function PhishingCampaignRecipientsAgGrid({
  label,
  rowData,
  loading,
  getStatusLabel,
  statusFilterValues,
  onSearchChange,
  onGridApi,
  onViewEmailBody,
}: PhishingCampaignRecipientsAgGridProps) {
  const { t } = useI18n();
  const gridApiRef = useRef<GridApi<PhishingRecipientRow> | null>(null);
  const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
    useClientExternalTextSearch<PhishingRecipientRow>({
      fields: [
        (row) => row.name,
        (row) => row.email,
        (row) => row.department,
        (row) => row.organization,
      ],
      onSearchChange,
      onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
    });

  const columnDefs = useMemo(
    () =>
      getPhishingRecipientColumns({
        label,
        getStatusLabel,
        statusFilterValues,
        onViewEmailBody,
      }),
    [label, getStatusLabel, statusFilterValues, onViewEmailBody],
  );

  const onGridReadyWrapped = useCallback(
    (event: { api: GridApi<PhishingRecipientRow> }) => {
      gridApiRef.current = event.api;
      onGridApi(event.api);
    },
    [onGridApi],
  );

  return (
    <AgGridBase.Root fullWidth>
      <AgGridBase.Header hidden />
      <AgGridBase.Table<PhishingRecipientRow>
        columnDefs={columnDefs}
        dataMode="client"
        rowData={rowData}
        showSidebar={false}
        movableColumns={false}
        bodyHeight={TABLE_BODY_HEIGHT}
        bodyMinHeight={DEFAULT_GRID_SIZING.minHeight}
        defaultColDef={{
          ...(DEFAULT_BASE_COL_DEF as ColDef<PhishingRecipientRow>),
          // Prevent header text from wrapping vertically when columns are narrow;
          // the grid will show a horizontal scrollbar instead.
          wrapHeaderText: false,
          autoHeaderHeight: false,
        }}
        showSearchBar={true}
        onSearchChange={handleSearchChange}
        agGridProps={{
          loading,
          getRowId: (p) => String(p.data?.id ?? ''),
          isExternalFilterPresent,
          doesExternalFilterPass,
          onGridReady: onGridReadyWrapped,
          onGridPreDestroyed: () => {
            gridApiRef.current = null;
            onGridApi(null);
          },
          noRowsOverlayComponent: EmptyState,
          noRowsOverlayComponentParams: {
            ImageSrc: EngageIcon,
            noDescription: false,
            description: t('PHISHING_CAMPAIGNS_RECIPIENTS_NO_ROWS_OVERLAY_DESCRIPTION'),
          },
        }}
      />
    </AgGridBase.Root>
  );
}
